import React, { useState, useEffect, useCallback } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';
import {
  onInstallStateChange, canPromptInstall, promptInstall, isStandalone, isIosSafari,
} from '../pwa';
import { useT } from '../i18n.jsx';

// Inviting somebody to add the portal to their home screen.
//
// Two surfaces, because they answer different questions. The banner appears once after signing in
// and asks "would you like this as an app"; the menu entry is always there and answers "how do I get
// this as an app" for the person who thought about it three weeks later. Dismissing the banner never
// removes the menu entry -- an invitation declined is not a feature withdrawn.
//
// Three states, not two:
//   installed          nothing to offer, and offering it anyway is how a product looks broken
//   promptable         Chrome, Edge, Android -- a real prompt exists and the browser hands it over
//   iOS Safari         no programmatic install exists at all, so the only honest thing is to say
//                      which two buttons to press

const DISMISS_KEY = 'install_banner_dismissed';

// Device-level rather than per-account on purpose: installing is a property of the device, so a
// parent who has already installed should not be asked again because their child signed in.
const wasDismissed = () => {
  try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
};
const rememberDismissed = () => {
  try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* private browsing */ }
};

export const useInstallState = () => {
  const [state, setState] = useState(() => ({
    installed: isStandalone(),
    promptable: canPromptInstall(),
    ios: isIosSafari(),
  }));

  useEffect(() => onInstallStateChange(() => setState({
    installed: isStandalone(),
    promptable: canPromptInstall(),
    ios: isIosSafari(),
  })), []);

  // Installing from an already-open tab does not reload it, so the banner has to notice on its own.
  useEffect(() => {
    const mq = window.matchMedia?.('(display-mode: standalone)');
    if (!mq?.addEventListener) return undefined;
    const onChange = () => setState((s) => ({ ...s, installed: isStandalone() }));
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return state;
};

const IosSteps = () => {
  const { t } = useT();
  return (
    <ol style={{ margin: '0.5rem 0 0', paddingLeft: '1.15rem', fontSize: '0.82rem', lineHeight: 1.9 }}>
      <li>
        <Share size={13} style={{ verticalAlign: '-2px' }} /> {t('install.iosShare')}
      </li>
      <li>
        <Plus size={13} style={{ verticalAlign: '-2px' }} /> {t('install.iosAdd')}
      </li>
    </ol>
  );
};

// Shown once, at the top of the content area, after signing in.
export const InstallBanner = ({ isMobile }) => {
  const { t } = useT();
  const { installed, promptable, ios } = useInstallState();
  const [dismissed, setDismissed] = useState(wasDismissed);
  const [showSteps, setShowSteps] = useState(false);

  const dismiss = useCallback(() => { rememberDismissed(); setDismissed(true); }, []);

  const install = useCallback(async () => {
    const outcome = await promptInstall();
    // Either way the browser will not offer this event again, so the banner has said its piece.
    if (outcome !== 'unavailable') dismiss();
  }, [dismiss]);

  if (installed || dismissed) return null;
  // Nothing to say on a desktop browser that has not offered a prompt: there is no instruction that
  // would work, and a banner explaining that would be worse than silence.
  if (!promptable && !ios) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        margin: isMobile ? '0.75rem' : '0 0 1rem',
        padding: '0.85rem 1rem',
        backgroundColor: 'var(--color-bg-card)',
        border: '1px solid var(--color-border)',
        borderLeft: '3px solid var(--color-accent)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <Download size={18} style={{ color: 'var(--color-primary)', flexShrink: 0, marginTop: '0.15rem' }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '0.92rem' }}>{t('install.title')}</div>
        <div style={{ fontSize: '0.82rem', color: 'var(--color-text-subtle)', marginTop: '0.15rem', lineHeight: 1.6 }}>
          {t('install.why')}
        </div>

        {ios && showSteps && <IosSteps />}

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.7rem', flexWrap: 'wrap' }}>
          {promptable ? (
            <button className="btn-primary" onClick={install} style={{ width: 'auto', padding: '0.4rem 0.9rem', fontSize: '0.85rem' }}>
              {t('install.install')}
            </button>
          ) : !showSteps && (
            <button className="btn-primary" onClick={() => setShowSteps(true)} style={{ width: 'auto', padding: '0.4rem 0.9rem', fontSize: '0.85rem' }}>
              {t('install.showMeHow')}
            </button>
          )}
          <button className="btn-outline" onClick={dismiss} style={{ width: 'auto', padding: '0.4rem 0.9rem', fontSize: '0.85rem' }}>
            {t('install.notNow')}
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={dismiss}
        aria-label={t('install.dismiss')}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.15rem', color: 'var(--color-text-muted)', display: 'flex', flexShrink: 0 }}
      >
        <X size={16} />
      </button>
    </div>
  );
};

// The permanent entry. Always present while the app is not installed, however many times the banner
// has been waved away.
export const InstallMenuItem = ({ compact = false }) => {
  const { t } = useT();
  const { installed, promptable, ios } = useInstallState();
  const [showSteps, setShowSteps] = useState(false);

  if (installed) return null;
  if (!promptable && !ios) return null;

  const label = t('install.installApp');

  if (compact) {
    return (
      <button
        type="button"
        onClick={() => (promptable ? promptInstall() : setShowSteps((v) => !v))}
        className="btn-outline"
        title={label}
        aria-label={label}
        style={{ width: 'auto', padding: '0.45rem', display: 'flex', alignItems: 'center', flexShrink: 0 }}
      >
        <Download size={16} />
      </button>
    );
  }

  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <button
        type="button"
        onClick={() => (promptable ? promptInstall() : setShowSteps((v) => !v))}
        className="btn-outline"
        style={{ width: '100%', padding: '0.5rem', fontSize: '0.875rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.4rem' }}
      >
        <Download size={16} /> {label}
      </button>
      {ios && showSteps && <IosSteps />}
    </div>
  );
};
